import React, { useEffect, useState } from "react";
import LeadSolveChatWidget from "./leadsolveWidget";
import ThreeCXWidget from "./3cxWidget";
import { isWithinWorkingHours } from "../../../helpers/WorkingHoursHelper";
import { usePortal } from "../../../context/PortalContext";
import { config as widgetConfig } from "./widgetContextConfig";
import { useLocation } from "react-router-dom";
import { useUserAuth } from "../../../context/AuthContext";
import { isHighFundLoggedOutQuoteAmount } from "../../../landers/Utils/highFundQuoteFlow";

const allowedLeadsolveProdDomain = "portal.pense.co.uk";
const LOGGED_OUT_HIGH_FUND_QUOTES_ROUTE = "/annuityquotesquickloggedout";
const LANDER_V2_CHAT_DISABLED_CAMPAIGNS = new Set([
    "annuitycalculator-ux2-vlm3",
]);

// LanderV2 route: /landers/v2/{campaignSlug}[/{step} | /google | /bing | /meta].
const LANDER_V2_CAMPAIGN_PATH_REGEX = /^\/landers\/v2\/([^/]+)(?:\/([^/]+))?/;
const LANDER_V2_SOURCE_SEGMENTS = ["google", "bing", "meta"];

// Step slugs that come before "quotes" for each LanderV2 campaign - widget stays
// hidden on the marketing/entry page and these input steps, and reappears from
// "quotes" onward (quotes, quote-detail, booking, etc.)
const LANDER_V2_PRE_QUOTES_STEPS: Record<string, string[]> = {
    "annuitycalculator-ux2-vlm3": ["personal-details"],
    "annuitycalculator-ux2-vlm1": ["quote-details", "address", "personal-details"],
    "drawdowncalculator-ux2-v1": ["personal-details"],
    "cost-of-delay-calculator": ["assumptions"],
};

const isLanderV2PreQuotesPath = (pathname: string): boolean => {
    const match = pathname.match(LANDER_V2_CAMPAIGN_PATH_REGEX);
    if (!match) {
        return false;
    }

    const [, campaignSlug, segment = ""] = match;
    const preQuotesSteps = LANDER_V2_PRE_QUOTES_STEPS[campaignSlug];
    if (!preQuotesSteps) {
        return false;
    }

    // No step (or an ad-source segment) = marketing/entry page, hidden there too
    const step = LANDER_V2_SOURCE_SEGMENTS.includes(segment) ? "" : segment;
    return !step || preQuotesSteps.includes(step);
};

const isLanderV2ChatDisabledPath = (pathname: string): boolean => {
    const match = pathname.match(LANDER_V2_CAMPAIGN_PATH_REGEX);
    return !!match && LANDER_V2_CHAT_DISABLED_CAMPAIGNS.has(match[1]);
};

export const useLeadSolveWidget = (enabled = true) => {
    const { clientData } = usePortal();
    const [isLeadSolveActive, setIsLeadSolveActive] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setIsLeadSolveActive(false);
            return;
        }

        const checkStatus = () => {
            if (!!sessionStorage.getItem("LeadsolveWidgetShown")) {
                setIsLeadSolveActive(true);
                return;
            }


            const isInWorkingHours = isWithinWorkingHours();
            const isLoggedIn = !!clientData?.annuity;
            const hasLeadsolveData = isLoggedIn && (!!clientData?.id || !!clientData?.email);

            setIsLeadSolveActive(((!isInWorkingHours && window.location.host === allowedLeadsolveProdDomain) || process.env.REACT_APP_ENV === "dev") && isLoggedIn && hasLeadsolveData);
        };

        checkStatus();
    }, [clientData, enabled]);

    return isLeadSolveActive;
}


const WidgetController = () => {
    const { clientData } = usePortal();
    const { user, loading: authLoading } = useUserAuth();
    const { pathname } = useLocation();

    const [locationContext, setLocationContext] = useState<string>("");
    const [clientContext, setClientContext] = useState<string>("");
    const [widgetContext, setWidgetContext] = useState<string>("");
    // Hidden on the logged-out results route only for the high-fund flow - a
    // repeat-enquiry, account-exists visitor gets the widget back, matching the
    // nav bar's lockNavigation rule in Navbar.tsx.
    const hideChatWidget =
        (pathname === LOGGED_OUT_HIGH_FUND_QUOTES_ROUTE &&
            sessionStorage.getItem("loggedOutQuickQuoteReason") !== "accountExists") ||
        isHighFundLoggedOutQuoteAmount(
            clientData?.annuity?.pensionFund,
            clientData?.annuity?.taxFreeCashTypeID
        ) ||
        isLanderV2ChatDisabledPath(pathname) ||
        isLanderV2PreQuotesPath(pathname);
    const showLeadSolve = useLeadSolveWidget(!hideChatWidget);

    useEffect(() => {
        const config = widgetConfig.find(item => item.path === pathname);
        setLocationContext(config?.context || "");
    }, [pathname]);

    useEffect(() => {
        if (authLoading || !user || !clientData) {
            setClientContext("");
            return;
        }

        const clientName = `Client Title: ${clientData.title || ""}; Client First Name: ${clientData.firstName || ""}; Client Last Name: ${clientData.lastName || ""}`.trim();
        const clientNumber = `Client Phone: ${clientData.primaryPhone || "Unknown"}`
        const clientEmail = `Client Email: ${clientData.email || "Unknown"}`
        const potSize = `Client Total Fund: ${clientData.annuity?.pensionFund || ""}`.trim();
        setClientContext(`${clientName}; ${clientNumber}; ${clientEmail}; ${potSize}`);
    }, [clientData, user, authLoading]);

    useEffect(() => {
        setWidgetContext(`${clientContext}; ${locationContext ? ("User Activity: " + locationContext) : ""}`);
    }, [clientContext, locationContext]);

    if (hideChatWidget) {
        return null;
    }

    return (<>
        {showLeadSolve ? (
            <LeadSolveChatWidget widgetContext={widgetContext} />
        ) : (
            <ThreeCXWidget />
        )}</>
    );
};

export default WidgetController;
